import {
  type PaidSpecContractType,
  type SpecContractStatusType,
  type SpecContractType,
} from '@/common/types/contract';

import blitzActivePng from '/img/contract/blitz-active.png';
import blitzActiveWebp from '/img/contract/blitz-active.webp';
import blitzDefaultPng from '/img/contract/blitz-default.png';
import blitzDefaultWebp from '/img/contract/blitz-default.webp';
import blitzExpiredPng from '/img/contract/blitz-expired.png';
import blitzExpiredWebp from '/img/contract/blitz-expired.webp';
import blitzFailPng from '/img/contract/blitz-fail.png';
import blitzFailWebp from '/img/contract/blitz-fail.webp';
import blitzLockPng from '/img/contract/blitz-lock.png';
import blitzLockWebp from '/img/contract/blitz-lock.webp';
import blitzNextPng from '/img/contract/blitz-next.png';
import blitzNextWebp from '/img/contract/blitz-next.webp';
import customDefaultPng from '/img/contract/custom-default.png';
import customDefaultWebp from '/img/contract/custom-default.webp';
import customLockPng from '/img/contract/custom-lock.png';
import customLockWebp from '/img/contract/custom-lock.webp';
import customNextPng from '/img/contract/custom-next.png';
import customNextWebp from '/img/contract/custom-next.webp';
import grandActivePng from '/img/contract/grand-active.png';
import grandActiveWebp from '/img/contract/grand-active.webp';
import grandDefaultPng from '/img/contract/grand-default.png';
import grandDefaultWebp from '/img/contract/grand-default.webp';
import grandExpiredPng from '/img/contract/grand-expired.png';
import grandExpiredWebp from '/img/contract/grand-expired.webp';
import grandFailPng from '/img/contract/grand-fail.png';
import grandFailWebp from '/img/contract/grand-fail.webp';
import grandLockPng from '/img/contract/grand-lock.png';
import grandLockWebp from '/img/contract/grand-lock.webp';
import grandNextPng from '/img/contract/grand-next.png';
import grandNextWebp from '/img/contract/grand-next.webp';
import premiumActivePng from '/img/contract/premium-active.png';
import premiumActiveWebp from '/img/contract/premium-active.webp';
import premiumDefaultPng from '/img/contract/premium-default.png';
import premiumDefaultWebp from '/img/contract/premium-default.webp';
import premiumExpiredPng from '/img/contract/premium-expired.png';
import premiumExpiredWebp from '/img/contract/premium-expired.webp';
import premiumFailPng from '/img/contract/premium-fail.png';
import premiumFailWebp from '/img/contract/premium-fail.webp';
import premiumLockPng from '/img/contract/premium-lock.png';
import premiumLockWebp from '/img/contract/premium-lock.webp';
import premiumNextPng from '/img/contract/premium-next.png';
import premiumNextWebp from '/img/contract/premium-next.webp';

const bgImage: Record<
  SpecContractType,
  Record<SpecContractStatusType, { img: string; webp: string }>
> = {
  Grand: {
    forViewing: {
      img: grandDefaultPng,
      webp: grandDefaultWebp,
    },
    lock: {
      img: grandLockPng,
      webp: grandLockWebp,
    },
    open: {
      img: grandDefaultPng,
      webp: grandDefaultWebp,
    },
    inProgress: {
      img: grandActivePng,
      webp: grandActiveWebp,
    },
    done: {
      img: grandActivePng,
      webp: grandActiveWebp,
    },
    expired: {
      img: grandExpiredPng,
      webp: grandExpiredWebp,
    },
    fail: {
      img: grandFailPng,
      webp: grandFailWebp,
    },
    wait: {
      img: grandNextPng,
      webp: grandNextWebp,
    },
  },
  Custom: {
    forViewing: {
      img: customDefaultPng,
      webp: customDefaultWebp,
    },
    lock: {
      img: customLockPng,
      webp: customLockWebp,
    },
    open: {
      img: customDefaultPng,
      webp: customDefaultWebp,
    },
    inProgress: {
      img: customDefaultPng,
      webp: customDefaultWebp,
    },
    done: {
      img: customDefaultPng,
      webp: customDefaultWebp,
    },
    expired: {
      img: '',
      webp: '',
    },
    fail: {
      img: '',
      webp: '',
    },
    wait: {
      img: customNextPng,
      webp: customNextWebp,
    },
  },
  Golden: {
    forViewing: {
      img: premiumDefaultPng,
      webp: premiumDefaultWebp,
    },
    lock: {
      img: premiumLockPng,
      webp: premiumLockWebp,
    },
    open: {
      img: premiumDefaultPng,
      webp: premiumDefaultWebp,
    },
    inProgress: {
      img: premiumActivePng,
      webp: premiumActiveWebp,
    },
    done: {
      img: premiumActivePng,
      webp: premiumActiveWebp,
    },
    expired: {
      img: premiumExpiredPng,
      webp: premiumExpiredWebp,
    },
    fail: {
      img: premiumFailPng,
      webp: premiumFailWebp,
    },
    wait: {
      img: premiumNextPng,
      webp: premiumNextWebp,
    },
  },
  Blitz: {
    forViewing: {
      img: blitzDefaultPng,
      webp: blitzDefaultWebp,
    },
    lock: {
      img: blitzLockPng,
      webp: blitzLockWebp,
    },
    open: {
      img: blitzDefaultPng,
      webp: blitzDefaultWebp,
    },
    inProgress: {
      img: blitzActivePng,
      webp: blitzActiveWebp,
    },
    done: {
      img: blitzActivePng,
      webp: blitzActiveWebp,
    },
    expired: {
      img: blitzExpiredPng,
      webp: blitzExpiredWebp,
    },
    fail: {
      img: blitzFailPng,
      webp: blitzFailWebp,
    },
    wait: {
      img: blitzNextPng,
      webp: blitzNextWebp,
    },
  },
};

const getBgImage = (
  kind: SpecContractType | PaidSpecContractType,
): Record<
  SpecContractStatusType,
  {
    img: string;
    webp: string;
  }
> => {
  switch (kind) {
    case 'Golden':
    case 'GoldenPlus': {
      return bgImage.Golden;
    }

    case 'Blitz':
    case 'BlitzPlus': {
      return bgImage.Blitz;
    }

    case 'Grand':
    case 'GrandPlus': {
      return bgImage.Grand;
    }

    default: {
      return bgImage[kind];
    }
  }
};

const titles: Record<SpecContractType | PaidSpecContractType, string> = {
  Custom: 'Custom',
  Golden: 'Premium',
  GoldenPlus: 'Premium +',
  Grand: 'Grand',
  GrandPlus: 'Grand +',
  Blitz: 'Blitz',
  BlitzPlus: 'Blitz + ',
};

export { bgImage, getBgImage, titles };
